import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

const TIPS = {
  social: [
    { target: '[data-testid="social-title"]', text: 'Your social feed — see posts from golfers you follow and share your rounds.', position: 'bottom' },
    { target: '[data-testid="nav-discover"]', text: 'Tap Discover to find and match with other golfers near you.', position: 'top' },
  ],
  discover: [
    { target: '[data-testid="discover-card"], [data-testid="discover-reload-btn"]', text: 'Swipe right to connect, left to pass. New golfers appear based on your preferences.', position: 'bottom' },
  ],
  scores: [
    { target: '[data-testid="add-score-btn"]', text: 'Log your rounds here — simple mode for just the score, or detailed with putts, FIR, and GIR.', position: 'bottom' },
  ],
  messages: [
    { target: '[data-testid="messages-list-page"]', text: 'Chat with your matches here. Plan tee times and get to know each other.', position: 'bottom' },
  ],
};

const TooltipGuide = ({ pageKey }) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [pos, setPos] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const rafRef = useRef(null);

  const storageKey = `club_tips_${pageKey}`;
  const tips = React.useMemo(() => TIPS[pageKey] || [], [pageKey]);

  useEffect(() => {
    if (localStorage.getItem(storageKey)) { setDismissed(true); return; }
    // Small delay to let page render
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [storageKey]);

  const updatePosition = useCallback(() => {
    if (dismissed || !tips[currentTip]) return;
    const selectors = tips[currentTip].target.split(', ');
    let el = null;
    for (const s of selectors) { el = document.querySelector(s); if (el) break; }
    if (!el) { setPos(null); return; }
    const r = el.getBoundingClientRect();
    setPos({
      top: r.top, left: r.left, width: r.width, height: r.height,
      tipPosition: tips[currentTip].position,
    });
  }, [currentTip, dismissed, tips]);

  useEffect(() => {
    if (!visible || dismissed) return;
    updatePosition();
    const interval = setInterval(updatePosition, 500);
    return () => clearInterval(interval);
  }, [visible, dismissed, updatePosition]);

  const dismiss = () => {
    if (currentTip < tips.length - 1) {
      setCurrentTip(c => c + 1);
    } else {
      localStorage.setItem(storageKey, 'true');
      setDismissed(true);
    }
  };

  const skipAll = () => {
    localStorage.setItem(storageKey, 'true');
    setDismissed(true);
  };

  if (dismissed || !visible || !tips.length || !pos) return null;

  const tipStyle = pos.tipPosition === 'top'
    ? { bottom: `${window.innerHeight - pos.top + 12}px`, left: '50%', transform: 'translateX(-50%)' }
    : { top: `${pos.top + pos.height + 12}px`, left: '50%', transform: 'translateX(-50%)' };

  return (
    <>
      {/* Backdrop — visual dim only. `pointer-events-none` so clicks pass through
          to the underlying UI (e.g. moderation kebab menus). Users dismiss via
          the Next / Skip all buttons on the tooltip card. */}
      <div
        className="fixed inset-0 z-[80] transition-opacity duration-300 pointer-events-none"
        style={{ background: 'rgba(8,36,28,0.45)' }}
        data-testid="tooltip-backdrop"
      />

      {/* Spotlight cutout */}
      <div
        className="fixed z-[81] rounded-xl transition-all duration-300 pointer-events-none"
        style={{
          top: pos.top - 4,
          left: pos.left - 4,
          width: pos.width + 8,
          height: pos.height + 8,
          boxShadow: '0 0 0 9999px rgba(8,36,28,0.45)',
          border: '2px solid var(--gold)',
        }}
      />

      {/* Tooltip */}
      <div
        className="fixed z-[82] w-72 p-4 rounded-xl shadow-xl animate-fade-in"
        style={{
          ...tipStyle,
          background: '#fff',
          border: '1px solid var(--fairway-sand)',
        }}
        data-testid="tooltip-content"
      >
        <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--deep-forest)' }}>
          {tips[currentTip].text}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: 'var(--warm-gray)' }}>
            {currentTip + 1} / {tips.length}
          </span>
          <div className="flex gap-2">
            {tips.length > 1 && (
              <button
                onClick={skipAll}
                className="text-xs px-3 py-1.5 rounded-lg"
                style={{ color: 'var(--warm-gray)' }}
              >
                Skip all
              </button>
            )}
            <button
              onClick={dismiss}
              className="text-xs font-semibold px-4 py-1.5 rounded-lg"
              style={{ background: 'var(--club-green)', color: '#fff' }}
              data-testid="tooltip-next"
            >
              {currentTip < tips.length - 1 ? 'Next' : 'Got it'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TooltipGuide;
